import { useApp } from 'contexts/app';
import { useDialog } from 'contexts/dialog';
import { useNavigationGuard } from 'contexts/navigationGuard';
import { useSnackbar } from 'contexts/snackbar';
import { createContext, useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import assignmentService from 'services/assignment';
import useBeforeUnload from 'services/hooks/useBeforeUnload';
import {
    DEFAULT_FEEDBACK_LANGUAGE_LEVEL,
    DEFAULT_FEEDBACK_NUM_SUGGESTIONS,
    DEFAULT_FEEDBACK_STRUCTURE,
    DEFAULT_FEEDBACK_SUGGESTION_LENGTH,
    DEFAULT_FEEDBACK_TONE,
} from 'utils/constants';
import { getAIPreview, getToneDescription } from 'utils/helper';

const PageContext = createContext();

const PageProvider = (props) => {
    const { course, setAssignments, currentAssignment } = useApp();
    const { setDialog, closeDialog } = useDialog();
    const history = useHistory();
    const { onBeforeNavigate } = useNavigationGuard();
    const { message } = useSnackbar();

    const [form, setForm] = useState(null);
    const [errors, setErrors] = useState({});
    const [selectRubricDialog, setSelectRubricDialog] = useState({ open: false });
    const [createRubricDialog, setCreateRubricDialog] = useState({ open: false });
    const [createRubricAIDialog, setCreateRubricAIDialog] = useState({ open: false });
    const [editRubricDialog, setEditRubricDialog] = useState({ open: false, customDefaultForm: null });
    const [editBaselineDialog, setEditBaselineDialog] = useState({ open: false });
    const [uploadContentBankResourcesDialog, setUploadContentBankResourcesDialog] = useState({ open: false });
    const [editContentBankResourcesDialog, setEditContentBankResourcesDialog] = useState({ open: false });
    const [previewUploadDialog, setPreviewUploadDialog] = useState({ open: false, target: null });
    const [previewFeedbackDialog, setPreviewFeedbackDialog] = useState({ open: false, output: null });
    const [feedbackSettingsDialog, setFeedbackSettingsDialog] = useState({ open: false });
    const [gradingSettingsDialog, setGradingSettingsDialog] = useState({ open: false });
    const [previewGradingDialog, setPreviewGradingDialog] = useState({ open: false, output: null });
    const [hasClickedSubmit, setHasClickedSubmit] = useState(false);

    const isFeedbackSettingsDefault = (form) => {
        return (
            form.structure === DEFAULT_FEEDBACK_STRUCTURE &&
            form.numSuggestions === DEFAULT_FEEDBACK_NUM_SUGGESTIONS &&
            form.suggestionLength === DEFAULT_FEEDBACK_SUGGESTION_LENGTH &&
            form.languageLevel === DEFAULT_FEEDBACK_LANGUAGE_LEVEL &&
            form.tone === DEFAULT_FEEDBACK_TONE
        );
    };

    const validateAll = useCallback(() => {
        let isValid = true;
        let newErrors = {};

        if (!form.title.trim()) {
            isValid = false;
            newErrors = { ...newErrors, title: true };
        }

        if (!form.description.trim()) {
            isValid = false;
            newErrors = { ...newErrors, description: true };
        }

        if (!form.rubric) {
            isValid = false;
            newErrors = { ...newErrors, rubric: true };
        }

        if (errors.feedbackSettings) {
            isValid = false;
            newErrors = { ...newErrors, feedbackSettings: errors.feedbackSettings };
        }

        if (errors.gradingSettings) {
            isValid = false;
            newErrors = { ...newErrors, gradingSettings: errors.gradingSettings };
        }

        setErrors(newErrors);

        return {
            isValid,
            errors: newErrors,
        };
    }, [errors.feedbackSettings, errors.gradingSettings, form?.description, form?.rubric, form?.title]);

    const onClickBack = () => {
        setDialog({
            open: true,
            message: 'You Have Unsaved Changes',
            description: 'If you exit without saving, all unsaved changes will be lost.',
            note: '',
            variant: 'destructive',
            actionButtonText: 'Exit',
            cancelButtonText: 'Cancel',
            onAction: () => {
                history.push(`/assignments/${currentAssignment.id}`);
                closeDialog();
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const updateAssignment = useCallback(async () => {
        setHasClickedSubmit(true);
        const { isValid, errors } = validateAll();
        if (!isValid) {
            return setDialog({
                open: true,
                message: 'Please complete all missing required fields before publishing.',
                description: (
                    <ul>
                        {errors.title && <li style={{ textAlign: 'left' }}>Assignment title</li>}
                        {errors.description && <li style={{ textAlign: 'left' }}>Assignment description</li>}
                        {errors.rubric && <li style={{ textAlign: 'left' }}>Assignment rubric</li>}
                        {errors.feedbackSettings && <li style={{ textAlign: 'left' }}>Feedback settings</li>}
                        {errors.gradingSettings && <li style={{ textAlign: 'left' }}>Feedback settings</li>}
                    </ul>
                ),
                note: '',
                variant: 'destructive',
                actionButtonText: 'Ok',
                cancelButtonText: 'Cancel',
                onAction: () => closeDialog(),
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        }

        const timestamp = new Date().getTime();
        const updatedAssignment = {
            modified_on: timestamp,
            course_id: course.id,
            title: form.title,
            description: form.description,
            rubric: form.rubric,
            baseline: form.baseline,
            content_bank_resources: form.contentBankResources,
            ai_feedback_enabled: form.aiFeedbackEnabled,
            ai_feedback_settings: {
                structure: form.feedbackSettings.structure,
                num_suggestions: form.feedbackSettings.numSuggestions,
                suggestion_length: form.feedbackSettings.suggestionLength,
                language_level: form.feedbackSettings.languageLevel,
                tone: form.feedbackSettings.tone,
                templated: form.feedbackSettings.templated,
                custom_instructions: form.feedbackSettings.customInstructions,
            },
            ai_feedback_context: form.feedbackCustomContext,
            ai_grading_enabled: form.aiGradingEnabled,
            ai_grading_settings: form.gradingSettings,
            ai_grading_context: form.gradingCustomContext,
            ai_student_feedback_enabled: form.aiStudentFeedbackEnabled,
            is_draft: false,
        };
        setAssignments((current) => {
            return current.map((a) => {
                return a.id !== currentAssignment.id ? a : { ...a, ...updatedAssignment };
            });
        });
        message.success('Your assignment was successfully updated.');
        history.push(`/assignments/${currentAssignment.id}`);
        await assignmentService.updateAssignment(currentAssignment.id, updatedAssignment);
    }, [
        closeDialog,
        course.id,
        currentAssignment?.id,
        form?.aiFeedbackEnabled,
        form?.aiGradingEnabled,
        form?.aiStudentFeedbackEnabled,
        form?.baseline,
        form?.contentBankResources,
        form?.description,
        form?.feedbackSettings,
        form?.feedbackCustomContext,
        form?.gradingCustomContext,
        form?.gradingSettings,
        form?.rubric,
        form?.title,
        history,
        message,
        setAssignments,
        setDialog,
        validateAll,
    ]);

    const unpublishAssignment = useCallback(async () => {
        const timestamp = new Date().getTime();
        const updatedAssignment = {
            modified_on: timestamp,
            course_id: course.id,
            title: form.title,
            description: form.description,
            rubric: form.rubric,
            baseline: form.baseline,
            content_bank_resources: form.contentBankResources,
            ai_feedback_enabled: form.aiFeedbackEnabled,
            ai_feedback_settings: {
                structure: form.feedbackSettings.structure,
                num_suggestions: form.feedbackSettings.numSuggestions,
                suggestion_length: form.feedbackSettings.suggestionLength,
                language_level: form.feedbackSettings.languageLevel,
                tone: form.feedbackSettings.tone,
                templated: form.feedbackSettings.templated,
                custom_instructions: form.feedbackSettings.customInstructions,
            },
            ai_feedback_context: form.feedbackCustomContext,
            ai_grading_enabled: form.aiGradingEnabled,
            ai_grading_settings: form.gradingSettings,
            ai_grading_context: form.gradingCustomContext,
            ai_student_feedback_enabled: form.aiStudentFeedbackEnabled,
            is_draft: true,
        };
        setAssignments((current) => {
            return current.map((a) => {
                return a.id !== currentAssignment.id ? a : { ...a, ...updatedAssignment };
            });
        });
        message.success('Your assignment was successfully updated.');
        history.push(`/assignments/${currentAssignment.id}`);
        await assignmentService.updateAssignment(currentAssignment.id, updatedAssignment);
    }, [
        course.id,
        currentAssignment?.id,
        form?.aiFeedbackEnabled,
        form?.aiGradingEnabled,
        form?.aiStudentFeedbackEnabled,
        form?.baseline,
        form?.contentBankResources,
        form?.description,
        form?.feedbackSettings,
        form?.feedbackCustomContext,
        form?.gradingCustomContext,
        form?.gradingSettings,
        form?.rubric,
        form?.title,
        history,
        message,
        setAssignments,
    ]);

    const onClickSave = useCallback(
        ({ key }) => {
            if (key === 'publish') {
                updateAssignment();
            }
            if (key === 'save') {
                unpublishAssignment();
            }
        },
        [updateAssignment, unpublishAssignment]
    );

    const onClickUploadPreview = async (files) => {
        const { target } = previewUploadDialog;
        setPreviewUploadDialog({ open: false, target: null });
        if (previewUploadDialog.target === 'feedback') {
            setPreviewFeedbackDialog({ open: true, output: null });
        }
        if (previewUploadDialog.target === 'grading') {
            setPreviewGradingDialog({ open: true, output: null });
        }
        try {
            const response = await getAIPreview({
                owner_account_id: form.owner_account_id,
                target,
                assignment_id: currentAssignment ? currentAssignment.id : form.id,
                description: form.description,
                rubric: form.rubric,
                baselineAssignment: form.baseline?.assignment,
                baselineCriteria: form.baseline?.criteria,
                content_bank_resources: form.contentBankResources,
                structure: form.feedbackSettings?.structure || DEFAULT_FEEDBACK_STRUCTURE,
                is_rag_ready: form.is_rag_ready || false,
                numSuggestions: form.feedbackSettings?.numSuggestions || DEFAULT_FEEDBACK_NUM_SUGGESTIONS,
                suggestionLength: form.feedbackSettings?.suggestionLength || DEFAULT_FEEDBACK_SUGGESTION_LENGTH,
                languageLevel: form.feedbackSettings?.languageLevel || DEFAULT_FEEDBACK_LANGUAGE_LEVEL,
                tone: form.feedbackSettings?.tone || DEFAULT_FEEDBACK_TONE,
                toneDescription: getToneDescription(form.feedbackSettings?.tone),
                customInstructions: form.feedbackSettings?.customInstructions || '',
                feedbackCustomContext: form.feedbackCustomContext,
                gradingSettings: form.gradingSettings,
                gradingCustomContext: form.gradingCustomContext,
                submission: files[0].data,
            });
            const result = await response.json();
            if (previewUploadDialog.target === 'feedback') {
                setPreviewFeedbackDialog({ open: true, output: result });
            }
            if (previewUploadDialog.target === 'grading') {
                setPreviewGradingDialog({ open: true, output: result });
            }
        } catch (e) {
            console.error(e);
            message.error('Something went wrong. Please try again.');
        }
    };

    useEffect(() => {
        if (hasClickedSubmit) {
            validateAll();
        }
    }, [hasClickedSubmit, validateAll]);

    useEffect(() => {
        if (currentAssignment) {
            setForm({
                title: currentAssignment.title,
                description: currentAssignment.description,
                rubric: currentAssignment.rubric,
                baseline: currentAssignment.baseline,
                contentBankResources: currentAssignment.content_bank_resources || [],
                aiFeedbackEnabled: currentAssignment.ai_feedback_enabled,
                feedbackSettings: currentAssignment.ai_feedback_settings
                    ? {
                          structure: currentAssignment.ai_feedback_settings.structure,
                          numSuggestions: currentAssignment.ai_feedback_settings.num_suggestions,
                          suggestionLength: currentAssignment.ai_feedback_settings.suggestion_length,
                          languageLevel: currentAssignment.ai_feedback_settings.language_level,
                          tone: currentAssignment.ai_feedback_settings.tone,
                          templated: currentAssignment.ai_feedback_settings.templated,
                          customInstructions: currentAssignment.ai_feedback_settings.custom_instructions,
                      }
                    : {
                          structure: DEFAULT_FEEDBACK_STRUCTURE,
                          numSuggestions: DEFAULT_FEEDBACK_NUM_SUGGESTIONS,
                          suggestionLength: DEFAULT_FEEDBACK_SUGGESTION_LENGTH,
                          languageLevel: DEFAULT_FEEDBACK_LANGUAGE_LEVEL,
                          tone: DEFAULT_FEEDBACK_TONE,
                          templated: {},
                          customInstructions: '',
                      },
                feedbackCustomContext: currentAssignment.ai_feedback_context,
                aiGradingEnabled: currentAssignment.ai_grading_enabled,
                gradingSettings: currentAssignment.ai_grading_settings,
                gradingCustomContext: currentAssignment.ai_grading_context,
                aiStudentFeedbackEnabled: currentAssignment.ai_student_feedback_enabled || false,
            });
        }
    }, [currentAssignment]);

    useEffect(() => {
        onBeforeNavigate((navigate) => {
            setDialog({
                open: true,
                message: 'You Have Unsaved Changes',
                description: 'If you exit without saving, all unsaved changes will be lost.',
                note: '',
                variant: 'destructive',
                actionButtonText: 'Exit',
                cancelButtonText: 'Cancel',
                onAction: () => {
                    closeDialog();
                    navigate();
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        });

        return () => {
            onBeforeNavigate(null);
        };
    }, [closeDialog, onBeforeNavigate, setDialog]);

    useBeforeUnload();

    return (
        <PageContext.Provider
            value={{
                form,
                setForm,
                errors,
                setErrors,
                selectRubricDialog,
                setSelectRubricDialog,
                createRubricDialog,
                setCreateRubricDialog,
                createRubricAIDialog,
                setCreateRubricAIDialog,
                editRubricDialog,
                setEditRubricDialog,
                editBaselineDialog,
                setEditBaselineDialog,
                uploadContentBankResourcesDialog,
                setUploadContentBankResourcesDialog,
                editContentBankResourcesDialog,
                setEditContentBankResourcesDialog,
                previewUploadDialog,
                setPreviewUploadDialog,
                previewFeedbackDialog,
                setPreviewFeedbackDialog,
                feedbackSettingsDialog,
                setFeedbackSettingsDialog,
                gradingSettingsDialog,
                setGradingSettingsDialog,
                previewGradingDialog,
                setPreviewGradingDialog,
                isFeedbackSettingsDefault,
                onClickBack,
                onClickSave,
                onClickUploadPreview,
            }}
            {...props}
        />
    );
};

export { PageContext };
export default PageProvider;
